import React from 'react';

import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { Layout } from '@components/Layout';
import { ConnectedStepper } from '@components/utilities/MembershipForm/ConnectedStepper';
import { readPolicyPromptJa } from '@data/membershipForm';
import { memberTermsOfUseJa } from '@data/termsOfUse';
import { usePatchCompany } from '@hooks/mutation/usePatchCompany';
import { useButtonStyles } from '@pages/utilities/MembershipForm/helpers';
import { AuthManager } from '@store/AuthManager';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { Link, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '規約のご同意',
    promptTitle: '新規取引確認フォームを入力して、D2Dを始めましょう',
    promptInfo: () => (
      <>
        D2D MEMBERSの申し込みには、無料アカウントが必要です。
        <br />
        まだ無料アカウントをお持ちでない方は「
        <Link to={url('/auth/CreateAccount')}>無料アカウントを作成</Link>
        」してください。
      </>
    ),
    readPolicyPrompt: readPolicyPromptJa,
    termsOfPolicyTitle: '利用規約',
    termsOfPolicyContent: memberTermsOfUseJa,
    disagree: '同意しない',
    agree: '同意する',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  promptTitle: {
    ...common.normalText,
    fontSize: 24,
    fontWeight: 500,
    color: theme.palette.primary.main,
    textAlign: 'center',
    marginBottom: 40,
  },
  promptInfo: {
    ...common.middleText,
    whiteSpace: 'pre-wrap',
    lineHeight: '42px',
    fontWeight: 500,
    padding: '20px 40px',
    backgroundColor: theme.palette.grey[50],
    borderRadius: 18,
    '& a': {
      color: theme.palette.primary.main,
    },
  },
  readPolicyPrompt: {
    ...common.middleText,
    whiteSpace: 'pre-wrap',
    color: theme.palette.text.secondary,
    marginBottom: 40,
  },
  bodyContainer: {
    ...common.flexColumn,
    padding: '0 1rem',
  },
  termsOfUsage: {
    ...common.middleText,
    backgroundColor: theme.palette.grey[50],
    height: 220,
    padding: '2rem',
    whiteSpace: 'pre-wrap',
    overflow: 'scroll',
  },
}));

export const PolicyAgreement: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useButtonStyles(),
    ...useStyles(),
  };

  const patchCompany = usePatchCompany();

  const history = useHistory();

  const authManager = AuthManager.shared;

  return (
    <Layout title={strings.title}>
      {() => (
        <Container>
          <div className={classes.promptTitle}>{strings.promptTitle}</div>

          {!authManager.isLoggedIn && (
            <div className={classes.promptInfo}>{strings.promptInfo()}</div>
          )}

          <div className={classes.bodyContainer}>
            <div style={{ height: 40 }} />
            <ConnectedStepper index={0} />
            <div style={{ height: 40 }} />

            <div className={classes.readPolicyPrompt}>{strings.readPolicyPrompt}</div>

            <div className={classes.termsOfUsage}>
              <div style={{ textAlign: 'center' }}>{strings.termsOfPolicyTitle}</div>
              {strings.termsOfPolicyContent}
            </div>

            <div className={classes.buttons}>
              <Button
                text={strings.disagree}
                color="primary"
                className={clsx(classes.button, classes.prev)}
                onClick={() => {
                  history.goBack();
                }}
              />
              <Button
                text={strings.agree}
                color="primary"
                className={clsx(classes.button, classes.next)}
                onClick={async () => {
                  await patchCompany.mutateAsync({ tos_agreed: true });
                  history.push('/utilities/MembershipForm/Information');
                  window.scrollTo(0, 0);
                }}
              />
            </div>
          </div>
        </Container>
      )}
    </Layout>
  );
});
